import { useContext } from 'react'

import {
  Box, Button, EntityMultiSelect, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import EntitySelector from '@components/EntitySelector'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import { PublicationPaginationRuleModel } from '@models/types'

import useBrandProvider from '@hooks/useBrandProvider'

import useManagePublicationPaginationRulesModal from './hooks/useManagePublicationPaginationRulesModal'

const MODAL_KEY = 'ManagePublicationPaginationRulesModal'

type ModalContextTypes = {
  modalData: {
    publicationPaginationRule: Partial<PublicationPaginationRuleModel>,
  },
  topModalKey: string,
}

type ModalCallbacks = {
  closeModal: () => void,
  manageAssignedPublications: (payload: HandleCallbackActionParams) => Promise<object>,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManagePublicationPaginationRulesModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, manageAssignedPublications } = callbacks || {}

  const modalContext = useContext<ModalContextTypes>(ModalContext)
  const { modalData, topModalKey } = modalContext

  const isTopModal = topModalKey === MODAL_KEY

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { publicationPaginationRule } = modalPayload

  const {
    callbacks: {
      loadMore,
      manageForPaginationRule,
      setSelectedPublicationIds,
      setState,
    },
    canLoadMore,
    filteredPublicationIds,
    filteredPublicationsCount,
    hasActiveFilters,
    loadingPublications,
    paginationRule,
    publications,
    saveEnabled,
    selectedPublicationIds,
    state: { provider_id, string },
    updating,
  } = useManagePublicationPaginationRulesModal(publicationPaginationRule)

  const manageAssignedPublicationsPayload = {
    callbacks: {
      action: manageForPaginationRule,
      afterAction: closeModal,
    },
    entityParams: {
      action: 'assign',
      pagination_rule_id: publicationPaginationRule?.pagination_rule_id,
      publication_ids: selectedPublicationIds,
    },
    requestOptions: {
      include_publication_pagination_rule_publication: true,
    },
    toastText: 'Publications Assigned',
  }

  const { callbacks: { addBrandProvider } } = useBrandProvider({ brand_id: paginationRule?.brand_id })

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header
        callbacks={callbacks}
        title="Assign"
        titleSecondLine="Publications"
      />

      <SidebarModal.Content>
        <Box flexDirection="column">
          <EntitySelector
            callbacks={{
              // @ts-expect-error TS2322
              selectItem: provider => setState({ provider_id: provider?.id }),
            }}
            entityKey="providers"
            label="Provider"
            searchForwardProps={{
              listAction: (
                // @ts-expect-error TS2741
                <EntitySelector.CreateEntity
                  onClick={() => addBrandProvider()}
                  strings={{ create: 'Assign' }}
                />
              ),
            }}
            searchRequestOptions={{
              assigned_brand_id: paginationRule?.brand_id,
              publication_providers: true,
            }}
            // @ts-expect-error TS2322
            selectedItemId={provider_id}
            shouldRefetch={isTopModal}
          />

          <Form.Field label="Publication Title" marginTop="large">
            <input
              name="string"
              onChange={e => setState({ string: e.target.value })}
              type="text"
              value={string}
            />
          </Form.Field>

          {/*
           // @ts-expect-error TS2741 */}
          <EntityMultiSelect
            filteredEntityIds={hasActiveFilters ? filteredPublicationIds : []}
            boxProps={{ marginTop: 'large' }}
            callbacks={{
              loadMore,
              setSelectedIds: setSelectedPublicationIds,
            }}
            canLoadMore={canLoadMore}
            entities={publications}
            entityCount={filteredPublicationsCount}
            loading={loadingPublications}
            selectedIds={selectedPublicationIds}
          />
        </Box>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          // @ts-expect-error TS2345
          onClick={() => manageAssignedPublications(manageAssignedPublicationsPayload)}
          size="large"
        >
          {saveEnabled ? 'Assign Publications' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManagePublicationPaginationRulesModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
